import React, { useRef, useState } from "react";

import { mdiCloseThick } from "@mdi/js";
import Icon from "@mdi/react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import {
  containerClasses,
  multiTagContainerClasses,
  multiTagInputInternalClasses,
  wrapperClasses,
} from "./styles";

import type { JSX } from "react";

import type { InputProps } from "./types";

const REGEX_SEPARATOR = /[\s\n]|Enter/;

export default function MultiTagInput({
  name,
  label,
  sublabel,
  isRequired,
  isReadOnly,
  className,
  isNoWrap,
  icon,
  isCompact,
  placeholder,
  isTooltipErrorMessage,
  validate,
}: InputProps): JSX.Element {
  const { t } = useTranslation("editor-errors");
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const error = get(errors, name);

  return (
    <FieldContainer
      className={className}
      isCompact={isCompact}
      isNoWrap={isNoWrap}
    >
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <Controller
        control={control}
        name={name}
        render={({ field }): JSX.Element => {
          const { onChange, onBlur, value = [] } = field;
          const tags = Array.isArray(value) ? value : [];

          const handleInputChange = (
            e: React.ChangeEvent<HTMLInputElement>,
          ): void => {
            setInputValue(e.target.value);
          };

          const handleInputKeyDown = (
            e: React.KeyboardEvent<HTMLInputElement>,
          ): void => {
            if (REGEX_SEPARATOR.test(e.key) && inputValue.trim()) {
              e.preventDefault();
              const newTags = inputValue
                .split(REGEX_SEPARATOR)
                .map((tag) => tag.trim())
                .filter(Boolean);

              if (newTags.length > 0) {
                const uniqueTags = [...new Set([...tags, ...newTags])];
                onChange(uniqueTags);
                setInputValue("");
              }
            }
          };

          const handleRemoveTag = (indexToRemove: number): void => {
            const updatedTags = tags.filter(
              (_: string, index: number) => index !== indexToRemove,
            );
            onChange(updatedTags);
          };

          const handleBlur = (): void => {
            if (inputValue.trim()) {
              const newTags = inputValue
                .split(REGEX_SEPARATOR)
                .map((tag) => tag.trim())
                .filter(Boolean);

              if (newTags.length > 0) {
                const uniqueTags = [...new Set([...tags, ...newTags])];
                onChange(uniqueTags);
                setInputValue("");
              }
            }
            onBlur();
          };

          return (
            <div className={wrapperClasses({ isCompact })}>
              <div className={containerClasses({ isFullWidth: true })}>
                <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.46875rem]">
                  {icon}
                </div>
                <div className={multiTagContainerClasses({ icon, isReadOnly })}>
                  {tags.map((tag: string, index: number) => (
                    <div
                      key={uuidv4()}
                      className="bg-muted flex items-center gap-[0.234375rem] rounded-[0.15625rem] py-[0.078125rem] pr-[0.15625rem] pl-[0.3125rem]"
                    >
                      <span className="text-md leading-6">{tag}</span>
                      {!isReadOnly && (
                        <button
                          aria-label={`Remove ${tag}`}
                          className="border-border bg-card text-muted-foreground hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive flex items-center justify-center rounded-[0.234375rem] border p-[0.078125rem]"
                          onClick={(): void => handleRemoveTag(index)}
                          type="button"
                        >
                          <Icon path={mdiCloseThick} size={0.5} />
                        </button>
                      )}
                    </div>
                  ))}
                  <input
                    ref={inputRef}
                    className={multiTagInputInternalClasses()}
                    data-tooltip-content={error?.message}
                    data-tooltip-id={`field-${name}-tooltip`}
                    disabled={isReadOnly}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    placeholder={tags.length === 0 ? placeholder : ""}
                    type="text"
                    value={inputValue}
                  />
                </div>
              </div>
              <ErrorMessage
                error={error}
                isCompact={isCompact}
                isTooltip={isTooltipErrorMessage}
                name={name}
              />
            </div>
          );
        }}
        rules={{
          required: isRequired && t("requiredField"),
          validate,
        }}
      />
    </FieldContainer>
  );
}
